import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

// RxJS
import { Observable } from 'rxjs';

// Models
import { Citation, Reference, Result, SearchResponse } from '../models/search-response.model';
import { StreamResponse } from '../models/stream-response.model';

// Config
import { TAS_LIB_CONFIG } from '../config/tas-lib.token';


@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private readonly _apiKey = signal('');
  private readonly _contextId = signal('');

  // Signal read-only (sostituiscono getApiKey / getContextId)
  readonly apiKey = this._apiKey.asReadonly();
  readonly contextId = this._contextId.asReadonly();

  private http = inject(HttpClient);
  private config = inject(TAS_LIB_CONFIG);

  setApiKey(apiKey: string) {
    this._apiKey.set(apiKey);
  }

  setContextId(contextId: string) {
    this._contextId.set(contextId);
  }

  getApiKey() {
    return this.apiKey;
  }

  getContextId() {
    return this.contextId;
  }

  search(query: string, sessionId?: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const apiKey = this.apiKey();
    if (apiKey) {
      headers = headers.set('X-API-Key', apiKey);
    }

    const body = {
      query: query,
      session: sessionId
    };

    return this.http.post<SearchResponse>(`${this.config.apiUrl}/${this.contextId()}`, body, { headers });
  }

  streamWithPost(query: string, sessionId?: string) {
    const body = {
      query: query,
      session: sessionId
    };

    return new Observable<string>(observer => {
      fetch(`${this.config.apiUrlStream}/${this.contextId()}`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey() && { 'X-API-Key': this.apiKey() })
        }
      })
        .then(response => {
          if (!response.ok || !response.body) {
            observer.error(`HTTP error: ${response.status}`);
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const read = (): void => {
            reader.read().then(({ done, value }) => {
              if (done) {
                observer.complete();
                return;
              }

              if (value) {
                buffer += decoder.decode(value, { stream: true });

                const events = buffer.split('\n\n');
                // Keep the last incomplete chunk in buffer
                buffer = events.pop() ?? '';

                for (const event of events) {
                  if (event.startsWith('data:')) {
                    const jsonStr = event.slice(5).trim();
                    try {
                      const data = JSON.parse(jsonStr);
                      observer.next(data);
                    } catch (e) {
                      console.error('Error parsing JSON from stream:', e, jsonStr);
                    }
                  }
                }
              }

              read(); // recursive call to keep the stream alive
            }).catch(err => {
              console.error('Stream read error:', err);
              observer.error(err);
            });
          };

          read();
        })
        .catch(err => {
          console.error('Fetch error:', err);
          observer.error(err);
        });
    });
  }

  streamWithEventSource(query: string, sessionId?: string) {
    return new Observable<StreamResponse>(observer => {
      const params = new URLSearchParams();
      params.set('query', query);

      const apiKey = this.apiKey();
      if (apiKey) {
        params.set('key', apiKey);
      }

      if (sessionId) {
        params.append('session', sessionId);
      }

      const url = `${this.config.apiUrlStreamGet}/${this.contextId()}?${params.toString()}`;
      const eventSource = new EventSource(url, { withCredentials: false });

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          observer.next(data);

          if (data.state === 'SUCCEEDED') {
            if (eventSource) {
              console.debug('closing the connection');
              eventSource.close();
            }
          }
        } catch (e) {
          console.error('Error parsing SSE JSON:', e, event.data);
        }
      };

      eventSource.onerror = (err) => {
        observer.error(err);
        eventSource.close();
      };

      // Teardown logic: chiudere l'EventSource quando l'Observable è completato o annullato
      return () => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          eventSource.close();
        }
      };
    });
  }

  makeCitations(answerText: string, citations: Citation[], results: Result[]): { section: string, reference: string | null }[] {
    const answerTextInBytes = new TextEncoder().encode(answerText);
    const decoder = new TextDecoder();

    type CitationGroup = { startIndex: number; endIndex: number; references: Reference[] };

    function groupCitations(citations: Citation[]): CitationGroup[] {
      const sorted = citations.slice().sort((a, b) => (a.startIndex ?? 0) - (b.startIndex ?? 0));
      const grouped: CitationGroup[] = [];

      let current = sorted[0];
      let currentStart = current.startIndex ?? 0;
      let currentEnd = current.endIndex;
      let currentRefs = current.references;

      for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        const nextStart = next.startIndex ?? 0;

        const sameRefs = JSON.stringify(currentRefs) === JSON.stringify(next.references);
        const isContiguous = nextStart <= currentEnd + 1;

        if (sameRefs && isContiguous) {
          currentEnd = Math.max(currentEnd, next.endIndex);
        } else {
          grouped.push({ startIndex: currentStart, endIndex: currentEnd, references: currentRefs });
          currentStart = nextStart;
          currentEnd = next.endIndex;
          currentRefs = next.references;
        }
      }

      grouped.push({ startIndex: currentStart, endIndex: currentEnd, references: currentRefs });

      return grouped;
    }

    const groupedCitations = groupCitations(citations);
    const citationsArray: { section: string; reference: string | null, referenceIndexes: number[] }[] = [];

    let lastEndIndex = 0;

    groupedCitations.forEach(group => {
      if (group.startIndex > lastEndIndex) {
        const initialSection = decoder.decode(answerTextInBytes.slice(lastEndIndex, group.startIndex));
        citationsArray.push({ section: initialSection, reference: null, referenceIndexes: [] });
      }

      const section = decoder.decode(answerTextInBytes.slice(group.startIndex, group.endIndex));

      const references = new Set<string>();
      const referenceIndexes: number[] = [];

      if (group.references) {
        group.references.forEach(ref => {
          const result = results[ref.result];
          const pageInfo = ref.page ? ` (Pagina ${ref.page})` : '';
          references.add(`${result.title}${pageInfo}`);
          referenceIndexes.push(ref.result + 1);
        });
      }

      citationsArray.push({
        section,
        reference: [...references].join('\n'),
        referenceIndexes
      });

      lastEndIndex = group.endIndex;
    });

    if (lastEndIndex < answerTextInBytes.length) {
      const finalSection = decoder.decode(answerTextInBytes.slice(lastEndIndex));
      citationsArray.push({ section: finalSection, reference: null, referenceIndexes: [] });
    }

    return citationsArray;
  }

  makeCitationTags(answerText: string, citations: Citation[], results: Result[]): {
    textWithTags: string;
    citationsList: {
      citationTag: string;
      referenceIndexes: number[];
      reference: string;
    }[];
  } {
    const answerTextInBytes = new TextEncoder().encode(answerText);
    const decoder = new TextDecoder();

    type CitationGroup = { startIndex: number; endIndex: number; references: Reference[] };

    function groupCitations(citations: Citation[]): CitationGroup[] {
      const sorted = citations.slice().sort((a, b) => (a.startIndex ?? 0) - (b.startIndex ?? 0));
      const grouped: CitationGroup[] = [];

      let current = sorted[0];
      let currentStart = current.startIndex ?? 0;
      let currentEnd = current.endIndex;
      let currentRefs = current.references;

      for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        const nextStart = next.startIndex ?? 0;
        const sameRefs = JSON.stringify(currentRefs) === JSON.stringify(next.references);
        const isContiguous = nextStart <= currentEnd + 1;

        if (sameRefs && isContiguous) {
          currentEnd = Math.max(currentEnd, next.endIndex);
        } else {
          grouped.push({ startIndex: currentStart, endIndex: currentEnd, references: currentRefs });
          currentStart = nextStart;
          currentEnd = next.endIndex;
          currentRefs = next.references;
        }
      }

      grouped.push({ startIndex: currentStart, endIndex: currentEnd, references: currentRefs });

      return grouped;
    }

    const groupedCitations = groupCitations(citations);
    const citationsList: {
      citationTag: string;
      referenceIndexes: number[];
      reference: string;
    }[] = [];

    let resultParts: string[] = [];
    let lastEndIndex = 0;

    groupedCitations.forEach((group, i) => {
      const before = decoder.decode(answerTextInBytes.slice(lastEndIndex, group.endIndex));
      resultParts.push(before);

      const citationTag = `[[citation-${i + 1}]]`;

      const references = new Set<string>();
      const referenceIndexes: number[] = [];

      group.references.forEach(ref => {
        const result = results[ref.result];
        const pageInfo = ref.page ? ` (Pagina ${ref.page})` : '';
        references.add(`${result.title}${pageInfo}`);
        referenceIndexes.push(ref.result + 1);
      });

      citationsList.push({
        citationTag,
        referenceIndexes,
        reference: [...references].join('\n')
      });

      resultParts.push(citationTag);
      lastEndIndex = group.endIndex;
    });

    if (lastEndIndex < answerTextInBytes.length) {
      const remaining = decoder.decode(answerTextInBytes.slice(lastEndIndex));
      resultParts.push(remaining);
    }

    return {
      textWithTags: resultParts.join(''),
      citationsList
    };
  }
}
